import { useCallback, useEffect, useState } from "react";
import { AlertCircle, Loader2, RefreshCw, Send, Webhook } from "lucide-react";

import { requestConnectorsApi } from "../../services/api";
import { formatDateTime } from "../../utils/dateUtils";
import { useToast } from "../../context/ToastContext";
import { plainReason } from "../../utils/mappingConsistency";

const STATUS_STYLES = {
  success: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300",
  failed: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300",
  pending: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300",
};

/**
 * Connectors linked to the request's template: the latest delivery of this
 * request to each, and Re-publish for one that was blocked or failed.
 *
 * A blocked delivery (e.g. its Map Payload no longer matched the template)
 * stores no payload, so the delivery log's Retry can't resend it. Re-publish
 * rebuilds the payload from the request's current values and the connector's
 * current Map Payload, after the mapping has been fixed.
 *
 * `refreshKey` reloads the list when the request changes (e.g. on approval);
 * `onFailedCountChange` feeds the Exports tab badge.
 */
export default function ConnectorDeliveriesPanel({
  requestId,
  requestStatus,
  isAdmin,
  refreshKey,
  onFailedCountChange,
}) {
  const toast = useToast();
  const [connectors, setConnectors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [republishingId, setRepublishingId] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await requestConnectorsApi.list(requestId);
      const list = res.data?.connectors || [];
      setConnectors(list);
      onFailedCountChange?.(list.filter((c) => c.latest_delivery?.status === "failed").length);
    } catch (e) {
      console.error("Failed to load connector deliveries", e);
    } finally {
      setLoading(false);
    }
  }, [requestId, onFailedCountChange]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const isPublished = ["approved", "completed"].includes((requestStatus || "").toLowerCase());

  const handleRepublish = async (connector) => {
    try {
      setRepublishingId(connector.webhook_id);
      const res = await requestConnectorsApi.republish(requestId, connector.webhook_id);
      const delivery = res.data?.delivery;
      if (delivery?.status === "failed") {
        toast.error(
          "Re-publish blocked",
          plainReason(delivery.error_message) || "The connector did not accept this request.",
        );
      } else {
        toast.success("Re-published", `The request was sent to ${connector.name}.`);
      }
      await load();
    } catch (e) {
      console.error(e);
      toast.error("Re-publish failed", e?.response?.data?.error || "Could not re-publish.");
    } finally {
      setRepublishingId(null);
    }
  };

  if (!loading && connectors.length === 0) return null;

  return (
    <div className="mb-5">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Connectors</h4>
        <button
          onClick={load}
          className="text-xs text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1"
        >
          <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>
      <div className="space-y-2">
        {connectors.map((c) => {
          const d = c.latest_delivery;
          const status = d?.status;
          const outOfSync = c.mapping_check?.status === "error";
          const canRepublish = isAdmin && isPublished && c.is_active && c.can_republish;
          return (
            <div
              key={c.webhook_id}
              className="rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <Webhook className="h-3.5 w-3.5 text-teal-500 flex-shrink-0" />
                    <p className="text-xs font-medium text-gray-700 dark:text-gray-300 truncate">
                      {c.name}
                    </p>
                    <span
                      className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                        STATUS_STYLES[status] ||
                        "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300"
                      }`}
                    >
                      {status === "success"
                        ? "Delivered"
                        : status === "failed"
                          ? "Failed"
                          : status === "pending"
                            ? "Sending"
                            : "Not sent"}
                    </span>
                    {outOfSync && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full font-medium bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300">
                        Mapping out of sync
                      </span>
                    )}
                  </div>
                  {d && (
                    <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-0.5">
                      {formatDateTime(d.completed_at || d.created_at)}
                    </p>
                  )}
                  {status === "failed" && d?.error_message && (
                    <p className="mt-1 text-[10px] text-red-600 dark:text-red-400 whitespace-pre-wrap break-words">
                      {d.error_message}
                    </p>
                  )}
                  {outOfSync && (
                    <p className="mt-1 flex items-start gap-1 text-[10px] text-red-600 dark:text-red-400">
                      <AlertCircle className="h-3 w-3 mt-px flex-shrink-0" />
                      <span>
                        {c.mapping_check.errors[0]?.message}
                        {c.mapping_check.errors.length > 1 &&
                          ` (+${c.mapping_check.errors.length - 1} more)`}
                        {" — publishing is blocked until this is fixed."}
                      </span>
                    </p>
                  )}
                </div>
                {canRepublish && (
                  <button
                    onClick={() => handleRepublish(c)}
                    disabled={republishingId === c.webhook_id}
                    title={
                      outOfSync
                        ? "Fix the Map Payload on the template first, or this will be blocked again"
                        : "Send this request to the connector again"
                    }
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-navy-800 text-white text-[10px] font-medium disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
                  >
                    {republishingId === c.webhook_id ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <Send className="h-3 w-3" />
                    )}
                    {d ? "Re-publish" : "Publish"}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
